import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Box,
  FormControlLabel,
  IconButton,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  fetchClient,
  fetchClientCreditsWithQuotas,
  fetchClientReferences,
} from "./gestionCarteraApi";
import {
  formatDocument,
  formatFullName,
  formatMoney,
  summarizeClientCredits,
  summarizeCredit,
  todayIso,
} from "./gestionCarteraFormat";
import { creditDetailPath, GESTION_CARTERA_PATH } from "./gestionCarteraRoutes";
import {
  Breadcrumbs,
  Card,
  cellSx,
  COLORS,
  CountPill,
  headCellSx,
  labelSx,
  linkSx,
  PageContainer,
  PrimaryButton,
  SectionTitle,
  SkeletonRows,
  StatusBadge,
  TableMessage,
} from "./gestionCarteraUi";
import EditarClienteModal from "./modals/EditarClienteModal";
import DocumentosModal from "./modals/DocumentosModal";
import ReferenciasModal from "./modals/ReferenciasModal";
import GestionCobranzaSection from "./GestionCobranzaSection";
import HistorialPagosSection from "./HistorialPagosSection";

const NO_REFERENCES = [];
const CREDIT_COLUMNS = ["ID crédito", "Comercio", "Valor crédito", "Saldo capital", "Saldo total (Saldo insoluto)", "Estado", "Acción"];

const useAsyncResource = (loader) => {
  const [resource, setResource] = useState({ status: "loading", data: null });
  const reload = useCallback(() => {
    setResource((current) => ({ ...current, status: "loading" }));
    loader()
      .then((data) => setResource({ status: "ready", data }))
      .catch(() => setResource({ status: "error", data: null }));
  }, [loader]);
  useEffect(() => {
    reload();
  }, [reload]);
  return [resource, reload];
};

function InfoItem({ label, value }) {
  return (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      <Typography sx={{ mt: 0.25, fontWeight: 500, wordBreak: "break-word" }}>{value || "—"}</Typography>
    </Box>
  );
}

function ContactFact({ icon: Icon, label, value }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <Icon aria-hidden="true" sx={{ fontSize: 20, color: COLORS.primary }} />
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.text, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>
        <Box component="span" sx={{ position: "absolute", width: "1px", height: "1px", p: 0, m: "-1px", overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: 0 }}>{label}</Box>
        {value || "—"}
      </Typography>
    </Box>
  );
}

function HolderCard({ client, onEdit, onDocuments }) {
  const fullName = formatFullName(client);
  const buttonSx = {
    minHeight: 28,
    px: 1.25,
    py: 0,
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 999,
    "& .MuiButton-startIcon": { mr: 0.5 },
    "& .MuiButton-startIcon svg": { fontSize: 16 },
  };
  return (
    <Card component="section" aria-label="Titular" sx={{ p: 2, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" sx={{ fontSize: 14, fontWeight: 700, color: COLORS.primary, lineHeight: 1.2 }}>{fullName}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, mt: 0.75 }}>
            <ContactFact icon={BadgeOutlinedIcon} label="Documento" value={formatDocument(client)} />
            <ContactFact icon={PhoneOutlinedIcon} label="Celular" value={client.cellphone} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: { xs: "100%", sm: "auto" } }}>
        <PrimaryButton startIcon={<VisibilityOutlinedIcon />} onClick={onEdit} sx={{ ...buttonSx, width: { xs: "100%", sm: "auto" } }}>Ver datos completos del cliente</PrimaryButton>
        <PrimaryButton startIcon={<DescriptionOutlinedIcon />} onClick={onDocuments} sx={{ ...buttonSx, width: { xs: "100%", sm: "auto" } }}>Ver documentos</PrimaryButton>
      </Box>
    </Card>
  );
}

function KpiFigure({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted }}>{label}</Typography>
      <Box sx={{ mt: 0.75 }}>{children}</Box>
    </Box>
  );
}

function ClientSummaryCard({ loading, error, activeCount, activeInMora, totalPending }) {
  const valueSx = { fontSize: 24, fontWeight: 500, color: COLORS.primary, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" };
  const cardSx = { p: 2, height: "100%", display: "flex", alignItems: "center", boxSizing: "border-box" };
  if (error) {
    return (
      <Card component="section" role="alert" aria-label="Saldo y créditos activos" sx={cardSx}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.danger }}>No pudimos calcular el saldo</Typography>
      </Card>
    );
  }

  return (
    <Card component="section" aria-label="Saldo y créditos activos" sx={{ ...cardSx, flexWrap: "wrap", gap: 3 }}>
      <KpiFigure label="Créditos activos">
        {loading ? <Skeleton variant="rounded" width={48} height={28} /> : (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            <Typography sx={valueSx}>{activeCount}</Typography>
            {activeInMora > 0 && <StatusBadge tone="red" label={`${activeInMora} en mora`} />}
          </Box>
        )}
      </KpiFigure>
      <KpiFigure label="Saldo total pendiente">
        {loading ? <Skeleton variant="rounded" width={160} height={28} /> : (
          <Typography sx={valueSx}>{formatMoney(totalPending)}</Typography>
        )}
      </KpiFigure>
    </Card>
  );
}

function CreditRow({ credit, clientUuid, today }) {
  const summary = summarizeCredit(credit, today);
  const detailPath = creditDetailPath(clientUuid, credit.uuid);
  return (
    <TableRow hover>
      <TableCell sx={cellSx}><Box component={RouterLink} to={detailPath} sx={linkSx}>{credit.oldId ?? "—"}</Box></TableCell>
      <TableCell sx={cellSx}>
        <Typography sx={{ fontWeight: 500 }}>{credit.commerce?.companyName || credit.commerce?.name || "—"}</Typography>
        <Typography sx={{ fontSize: 14, color: COLORS.muted }}>{credit.commerce?.industry || ""}</Typography>
      </TableCell>
      <TableCell sx={cellSx}>{formatMoney(credit.totalValue)}</TableCell>
      <TableCell sx={cellSx}>{formatMoney(credit.saldoCapital)}</TableCell>
      <TableCell sx={cellSx}>{formatMoney(credit.balanceCredit)}</TableCell>
      <TableCell sx={cellSx}><StatusBadge tone={summary.state.tone} label={summary.state.label} /></TableCell>
      <TableCell sx={cellSx}>
        <Tooltip title="Ver detalle">
          <IconButton component={RouterLink} to={detailPath} aria-label="Ver detalle" sx={{ color: COLORS.primary }}><AssignmentIcon /></IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function CreditsSection({ creditsResource, clientUuid, today, onRetry }) {
  const [showInactive, setShowInactive] = useState(false);
  const credits = creditsResource.data || [];
  const shown = credits.filter((credit) => showInactive || summarizeCredit(credit, today).active);

  const renderBody = () => {
    if (creditsResource.status === "loading") return <SkeletonRows columns={CREDIT_COLUMNS.length} rows={3} />;
    if (creditsResource.status === "error") {
      return <TableMessage columns={CREDIT_COLUMNS.length} role="alert" title="No pudimos cargar los créditos" action={<PrimaryButton onClick={onRetry}>Reintentar</PrimaryButton>} />;
    }
    if (shown.length === 0) {
      return <TableMessage columns={CREDIT_COLUMNS.length} title="El cliente no tiene créditos activos" description="Activa «Mostrar créditos no activos» para ver el historial." />;
    }
    return shown.map((credit) => <CreditRow key={credit.uuid} credit={credit} clientUuid={clientUuid} today={today} />);
  };

  return (
    <Card component="section" aria-label="Créditos del cliente" sx={{ overflow: "hidden" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5, p: { xs: "12px", sm: "16px 20px" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <SectionTitle>Créditos del cliente</SectionTitle>
          {creditsResource.status === "ready" && <CountPill value={shown.length} />}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2.5 }}>
          <FormControlLabel
            control={<Switch checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />}
            label="Mostrar créditos no activos"
            sx={{ ml: 0, mr: 0, maxWidth: "100%" }}
          />
        </Box>
      </Box>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1120 }} aria-label="Créditos del cliente">
          <TableHead>
            <TableRow>{CREDIT_COLUMNS.map((column) => <TableCell key={column} sx={headCellSx}>{column}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

function ReferencesSection({ referencesResource, onEdit }) {
  const references = referencesResource.data || [];
  return (
    <Card component="section" aria-label="Referencias" sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <SectionTitle>Referencias</SectionTitle>
          <CountPill value={references.length} />
        </Box>
        <PrimaryButton startIcon={<EditIcon />} onClick={onEdit} disabled={referencesResource.status !== "ready"} sx={{ borderRadius: 999, width: { xs: "100%", sm: "auto" } }}>
          Editar referencias
        </PrimaryButton>
      </Box>
      {referencesResource.status === "loading" && <Skeleton variant="rounded" height={90} sx={{ mt: 2.5 }} />}
      {referencesResource.status === "error" && <Typography role="alert" sx={{ mt: 2.5, color: COLORS.danger }}>No pudimos cargar las referencias.</Typography>}
      {referencesResource.status === "ready" && references.length === 0 && (
        <Typography sx={{ mt: 2.5, color: COLORS.muted }}>El cliente no tiene referencias registradas.</Typography>
      )}
      {references.length > 0 && (
        <Box component="ul" sx={{ listStyle: "none", m: "20px 0 0", p: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fit, minmax(280px, 1fr))" }, gap: 2 }}>
          {references.map((reference) => (
            <Box component="li" key={reference.uuid} sx={{ border: `1px solid ${COLORS.border}`, borderRadius: "12px", p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography sx={{ fontWeight: 500 }}>{formatFullName(reference)}</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                <InfoItem label="Celular" value={reference.cellphone} />
                <InfoItem label="Correo" value={reference.email} />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Card>
  );
}

/** Pantalla 2 · Perfil del cliente. */
export default function GestionCarteraCliente() {
  const { clientUuid } = useParams();
  const today = todayIso();
  const [openModal, setOpenModal] = useState(null);

  const [clientResource, reloadClient] = useAsyncResource(useCallback(() => fetchClient(clientUuid), [clientUuid]));
  const [creditsResource, reloadCredits] = useAsyncResource(useCallback(() => fetchClientCreditsWithQuotas(clientUuid), [clientUuid]));
  const [referencesResource, reloadReferences] = useAsyncResource(useCallback(() => fetchClientReferences(clientUuid), [clientUuid]));

  const client = clientResource.data;
  const credits = useMemo(() => creditsResource.data || [], [creditsResource.data]);
  const kpis = summarizeClientCredits(credits, today);
  const closeModal = useCallback(() => setOpenModal(null), []);

  if (clientResource.status === "error") {
    return (
      <PageContainer>
        <Breadcrumbs items={[{ label: "Clientes", to: GESTION_CARTERA_PATH }, { label: "Cliente" }]} />
        <Card role="alert" sx={{ p: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.primary }}>No pudimos cargar el cliente</Typography>
          <PrimaryButton sx={{ mt: 2 }} onClick={reloadClient}>Reintentar</PrimaryButton>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Breadcrumbs items={[{ label: "Clientes", to: GESTION_CARTERA_PATH }, { label: client ? formatFullName(client) : "Cargando…" }]} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: 2.5, alignItems: "stretch" }}>
        {client ? (
          <HolderCard client={client} onEdit={() => setOpenModal("editar")} onDocuments={() => setOpenModal("documentos")} />
        ) : (
          <Skeleton variant="rounded" height={160} />
        )}
        <ClientSummaryCard
          loading={creditsResource.status === "loading"}
          error={creditsResource.status === "error"}
          activeCount={kpis.activeCount}
          activeInMora={kpis.activeInMora}
          totalPending={kpis.totalPending}
        />
      </Box>

      <CreditsSection creditsResource={creditsResource} clientUuid={clientUuid} today={today} onRetry={reloadCredits} />
      <HistorialPagosSection clientUuid={clientUuid} credits={credits} />
      <ReferencesSection referencesResource={referencesResource} onEdit={() => setOpenModal("referencias")} />
      <GestionCobranzaSection
        clientUuid={clientUuid}
        credits={credits}
      />

      <EditarClienteModal
        open={openModal === "editar"}
        clientUuid={clientUuid}
        onClose={closeModal}
        onSaved={reloadClient}
      />
      <DocumentosModal open={openModal === "documentos"} client={client} onClose={closeModal} onSaved={reloadClient} />
      <ReferenciasModal
        open={openModal === "referencias"}
        client={client}
        references={referencesResource.data || NO_REFERENCES}
        onClose={closeModal}
        onSaved={() => {
          closeModal();
          reloadReferences();
        }}
      />
    </PageContainer>
  );
}
